import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ArrowRight, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/hooks';
import { supabase } from '../../../lib/supabase';
import { Product, ProductFormData } from './types';
import { validateProductForm } from './validation';
import { ProductForm } from './components/ProductForm';
import { ProductCard } from './components/ProductCard';
import toast from 'react-hot-toast';

const defaultFormData: ProductFormData = {
  name_he: '',
  sku: '',
  barcode: '',
  price: '',
  stock_quantity: '0',
  min_stock_quantity: '0',
  image: null,
  description: '',
  promotion: undefined
};

function BusinessProducts() {
  const { user, business, loading: authLoading } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(defaultFormData);

  useEffect(() => {
    const loadProducts = async () => {
      if (authLoading) return;

      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        let businessId = business?.id;
        if (!businessId) {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('business_id')
            .eq('id', user.id)
            .single();

          if (userError) throw userError;
          businessId = userData?.business_id;
        }

        if (!businessId) {
          throw new Error('לא נמצא עסק מקושר');
        }

        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('business_id', businessId)
          .order('created_at');

        if (error) throw error;
        setProducts(data || []);
      } catch (error: any) {

        toast.error('שגיאה בטעינת המוצרים');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [user?.id, business?.id, authLoading]);

  const uploadImage = async (file: File, businessId: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}${Date.now()}.${fileExt}`;
      const filePath = `${businessId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('products')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('products')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {

      throw new Error('שגיאה בהעלאת התמונה');
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      name_he: product.name_he,
      sku: product.sku || '',
      barcode: product.barcode || '',
      price: product.price.toString(),
      stock_quantity: product.stock_quantity.toString(),
      min_stock_quantity: product.min_stock_quantity.toString(),
      description: product.description || '',
      promotion: product.promotion
      // לא מאתחלים את שדה התמונה בכלל כשעורכים מוצר קיים
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const validationError = validateProductForm(formData);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    try {
      let businessId = business?.id;
      
      if (!businessId) {
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('business_id')
          .eq('id', user?.id)
          .single();

        if (userError) throw new Error('לא נמצא עסק מקושר');
        businessId = userData?.business_id;
      }

      if (!businessId) {
        throw new Error('לא נמצא עסק מקושר');
      }

      let imageUrl = editingProduct?.image_url;

      // טיפול בתמונה רק אם יש שינוי בשדה התמונה
      if ('image' in formData) {
        // אם ביקשנו במפורש למחוק את התמונה
        if (formData.image === null) {
          if (editingProduct?.image_url) {
            const oldPath = editingProduct.image_url.split('/').pop();
            if (oldPath) {
              await supabase.storage
                .from('products')
                .remove([`${businessId}/${oldPath}`]);
            }
          }
          imageUrl = null;
        }
        // אם יש תמונה חדשה להעלות
        else if (formData.image instanceof File) {
          // מחיקת התמונה הקיימת אם יש
          if (editingProduct?.image_url) {
            const oldPath = editingProduct.image_url.split('/').pop();
            if (oldPath) {
              await supabase.storage
                .from('products')
                .remove([`${businessId}/${oldPath}`]);
            }
          }
          // העלאת התמונה החדשה
          imageUrl = await uploadImage(formData.image, businessId);
        }
      }

      const productData = {
        business_id: businessId,
        name_he: formData.name_he,
        name: formData.name_he,
        sku: formData.sku || null,
        barcode: formData.barcode || null,
        price: parseFloat(formData.price),
        stock_quantity: parseInt(formData.stock_quantity),
        min_stock_quantity: parseInt(formData.min_stock_quantity),
        image_url: imageUrl,
        description: formData.description,
        promotion: formData.promotion
      };

      if (editingProduct) {
        const { error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id);

        if (error) throw error;
        toast.success('המוצר עודכן בהצלחה');
      } else {
        const { error } = await supabase
          .from('products')
          .insert([productData]);

        if (error) throw error;
        toast.success('המוצר נוסף בהצלחה');
      }

      setShowForm(false);
      setEditingProduct(null);
      setFormData(defaultFormData);
      
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('business_id', businessId)
        .order('created_at');

      if (error) throw error;
      setProducts(data || []);
    } catch (error: any) {

      toast.error(error.message || (editingProduct ? 'שגיאה בעדכון המוצר' : 'שגיאה בהוספת המוצר'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק את המוצר?')) return;

    try {
      const product = products.find(p => p.id === id);
      
      if (product?.image_url) {
        const businessId = business?.id;
        const oldPath = product.image_url.split('/').pop();
        if (businessId && oldPath) {
          await supabase.storage
            .from('products')
            .remove([`${businessId}/${oldPath}`]);
        }
      }

      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('המוצר נמחק בהצלחה');
      
      const { data, error: loadError } = await supabase
        .from('products')
        .select('*')
        .eq('business_id', business?.id)
        .order('created_at');

      if (loadError) throw loadError;
      setProducts(data || []);
    } catch (error) {
      toast.error('שגיאה במחיקת המוצר');
    }
  };

  useEffect(() => {
    const checkLocalStorageForProducts = async () => {
      const registrationDraft = localStorage.getItem('registration_draft');

      if (registrationDraft) {
        try {
          const parsedDraft = JSON.parse(registrationDraft);
          const products = parsedDraft.step4?.products;

          if (Array.isArray(products) && products.length > 0) {
            let businessId = business?.id;

            if (!businessId) {
              const { data: userData, error: userError } = await supabase
                .from('users')
                .select('business_id')
                .eq('id', user?.id)
                .single();

              if (userError) throw userError;
              businessId = userData?.business_id;
            }

            if (!businessId) {
              throw new Error('לא נמצא עסק מקושר');
            }

            for (const product of products) {
              const productData = {
                business_id: businessId,
                name_he: product.name_he,
                name: product.name_he,
                sku: product.sku || null,
                barcode: product.barcode || null,
                price: parseFloat(product.price),
                stock_quantity: parseInt(product.stock_quantity),
                min_stock_quantity: parseInt(product.min_stock_quantity),
                description: product.description || '',
                image_url: null, // Assuming no image is provided
                promotion: null
              };

              const { error } = await supabase
                .from('products')
                .insert([productData]);

              if (error) throw error;
            }

            toast.success('המוצרים נוספו בהצלחה');
            localStorage.removeItem('registration_draft');

            const { data, error: loadError } = await supabase
              .from('products')
              .select('*')
              .eq('business_id', businessId)
              .order('created_at');

            if (loadError) throw loadError;
            setProducts(data || []);
          }
        } catch (error) {

        }
      }
    };

    checkLocalStorageForProducts();
  }, [business, user]);

  if (loading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center pb-24">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0 bg-gray-50">
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings" className="text-gray-500 active:text-gray-700">
                <ArrowRight className="h-6 w-6" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">מוצרים</h1>
            </div>
            <button
              onClick={() => {
                setEditingProduct(null);
                setFormData(defaultFormData);
                setShowForm(true);
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 0);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg active:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5" />
              <span className="text-sm font-medium">הוסף</span>
            </button>
          </div>
        </div>

        {showForm && (
          <ProductForm
            formData={formData}
            onFormChange={setFormData}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingProduct(null);
              setFormData(defaultFormData);
            }}
            isEditing={!!editingProduct}
            existingImageUrl={editingProduct?.image_url}
          />
        )}

        {products.length > 0 ? (
          <div className="px-4 pt-2 space-y-3">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        ) : !showForm && (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-center space-y-3">
              <p className="text-gray-500 text-sm">לא נמצאו מוצרים</p>
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setFormData(defaultFormData);
                  setShowForm(true);
                }}
                className="text-blue-600 active:text-blue-700 text-sm font-medium"
              >
                הוסף מוצר חדש
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default BusinessProducts;